import { useCallback, useEffect, useState } from 'react';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Badge, Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, shortDate, timeAgo } from '../../utils/format';

const NEXT = {
  requested: [
    { status: 'accepted', label: 'Accept', variant: 'success' },
    { status: 'rejected', label: 'Decline', variant: 'danger' },
  ],
  accepted: [
    { status: 'on_the_way', label: 'On the way', variant: 'primary' },
    { status: 'cancelled', label: 'Cancel', variant: 'danger' },
  ],
  on_the_way: [{ status: 'in_progress', label: 'Start work (OTP)', variant: 'primary' }],
  in_progress: [{ status: 'completed', label: 'Mark completed', variant: 'success' }],
};

const FILTERS = [
  { key: 'true', label: '🔥 Active' },
  { key: '', label: 'All' },
  { key: 'completed', label: '✅ Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const PartnerBookingsPage = () => {
  const toast = useToast();
  const [filter, setFilter] = useState('true');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otpFor, setOtpFor] = useState(null);
  const [otp, setOtp] = useState('');

  const load = useCallback(() => {
    const params = filter === 'true' ? { active: 'true' } : filter ? { status: filter } : {};
    api.bookings.partner({ ...params, limit: 40 })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [load]);

  const update = async (booking, status, extra = {}) => {
    try {
      await api.bookings.setStatus(booking._id, { status, ...extra });
      setOtpFor(null);
      setOtp('');
      load();
    } catch (err) {
      toast.error(err.message, 'Could not update');
    }
  };

  const act = async (booking, action) => {
    // Starting the work needs the OTP the customer is holding.
    if (action.status === 'in_progress') { setOtpFor(booking._id); return; }
    if (['rejected', 'cancelled'].includes(action.status)) {
      const ok = await toast.confirm({
        title: `${action.label} booking`,
        message: `${action.label} ${booking.bookingNo}?`,
        confirmLabel: action.label,
        danger: true,
      });
      if (!ok) return;
      update(booking, action.status, { note: 'Declined by provider' });
      return;
    }
    update(booking, action.status);
  };

  return (
    <div>
      <PageHeader
        title="Bookings"
        back={false}
        subtitle={`${items.length} ${filter === 'true' ? 'active' : 'total'}`}
      />

      <div className="row no-bar" style={{ gap: 8, overflowX: 'auto', marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <Chip key={f.key || 'all'} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
      </div>

      {loading ? <Loading /> : !items.length ? (
        <Empty emoji="📅" title="No bookings here" text="Customer service requests will appear here." />
      ) : (
        <div className="grid grid-list">
          {items.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 45)} className="card sh-sm">
              <div className="row-between">
                <div className="grow">
                  <div className="t-title">{item.serviceTitle}</div>
                  <div className="t-tiny">{item.bookingNo} · {timeAgo(item.createdAt)}</div>
                </div>
                <Badge value={item.status} />
              </div>

              <div
                className="row"
                style={{
                  gap: 10, background: colors.primaryLight, padding: 12,
                  borderRadius: 12, marginTop: 12,
                }}
              >
                <span style={{ fontSize: 16 }}>📅</span>
                <div className="grow">
                  <div className="t-body">{shortDate(item.scheduledDate)}</div>
                  <div className="t-tiny">{item.scheduledSlot || 'Any time'}</div>
                </div>
                <span className="t-title">{currency(item.totalAmount)}</span>
              </div>

              <div
                className="row"
                style={{
                  gap: 10, background: colors.bg, padding: 12,
                  borderRadius: 12, marginTop: 8, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 15 }}>👤</span>
                <div className="grow">
                  <div className="t-body">{(item.address && item.address.name) || (item.user && item.user.name) || 'Customer'}{item.address && item.address.phone ? ` · ${item.address.phone}` : ''}
                    {item.address && item.address.forOther ? (
                      <div className="t-tiny" style={{ color: 'var(--accent)', fontWeight: 700 }}>👥 Booked by {(item.user && item.user.name) || 'a customer'}{item.user && item.user.phone ? ` (${item.user.phone})` : ''} for this person</div>
                    ) : null}</div>
                  <div className="t-tiny clamp-2">
                    📍 {[
                      item.address && item.address.line1,
                      item.address && item.address.landmark,
                      item.address && item.address.city,
                    ].filter(Boolean).join(', ')}
                  </div>
                </div>
                <a
                  href={`tel:${(item.address && item.address.phone) || (item.user && item.user.phone)}`}
                  className="press"
                  style={{
                    width: 34, height: 34, borderRadius: 10, background: '#fff',
                    display: 'grid', placeItems: 'center', flex: '0 0 auto',
                  }}
                  aria-label="Call customer"
                >
                  📞
                </a>
              </div>

              {item.requirement ? (
                <div style={{ background: colors.warningBg, padding: 12, borderRadius: 12, marginTop: 8 }}>
                  <span className="t-small">📝 {item.requirement}</span>
                </div>
              ) : null}
              {item.vehicleInfo || item.pickupDrop ? (
                <div className="card" style={{ marginTop: 8, padding: 12, background: 'var(--primary-light)' }}>
                  <div className="t-small" style={{ fontWeight: 700 }}>
                    {item.pickupDrop ? '🚚 Pickup & drop requested' : '🏪 Customer brings the vehicle'}
                  </div>
                  {item.vehicleInfo ? <div className="t-small">🚗 {item.vehicleInfo}</div> : null}
                </div>
              ) : null}

              {otpFor === item._id ? (
                <div style={{ marginTop: 12 }}>
                  <Input
                    label="Enter the 4 digit OTP from the customer"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="0000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    style={{ marginBottom: 8 }}
                  />
                  <div className="row" style={{ gap: 10 }}>
                    <Button
                      title="Cancel"
                      variant="light"
                      size="sm"
                      full
                      onClick={() => { setOtpFor(null); setOtp(''); }}
                    />
                    <Button
                      title="Start work"
                      size="sm"
                      full
                      disabled={otp.length !== 4}
                      onClick={() => update(item, 'in_progress', { otp })}
                    />
                  </div>
                </div>
              ) : NEXT[item.status] ? (
                <div className="row" style={{ gap: 10, marginTop: 12 }}>
                  {NEXT[item.status].map((a) => (
                    <Button
                      key={a.status}
                      title={a.label}
                      variant={a.variant}
                      size="sm"
                      full
                      onClick={() => act(item, a)}
                    />
                  ))}
                </div>
              ) : null}
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerBookingsPage;
